<template>
  <div
    id="patient-div"
    class="element patient"
  >
    <div class="single-patient">
      <b-button
        class="d-flex justify-content-between"
        :class="{'attach-pressed': btnPressed}"
      >
        <div
          class="d-flex align-items-center flex-fill"
          @click="elementSelected"
        >
          <i
            class="far fa-user primary-dark-color mr-3"
            aria-hidden="true"
          />
          <span>{{ patientAttachmentTree.personIdentifier }}</span>
          <vtBubble
            v-if="patientAttachmentTree.attachmentsCount"
            :number="patientAttachmentTree.attachmentsCount"
            :type="false"
            :tooltip="' '+$tc('global.file',2)"
            style="margin-top: 2px"
          />
        </div>
        <div
          class="attach-arrow"
          @click="hideCases = !hideCases"
        >
          <i
            v-show="hideCases"
            class="far fa-chevron-down"
            aria-hidden="true"
          />
          <i
            v-show="!hideCases"
            class="far fa-chevron-up"
            aria-hidden="true"
          />
        </div>
      </b-button>

      <div
        v-for="(caseStudy, caseIndex) in patientAttachmentTree.cases"
        :key="caseIndex"
        class="element cases"
        :class="{'attach-display': hideCases}"
      >
        <case-tree
          ref="caseTree"
          :laboratory-id="laboratoryId"
          :case-study="caseStudy"
          @child-selected="childSelected($event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import patientAttachmentService from '@/api/resources/attachment/patientAttachmentService'
import CaseTree from './CaseTree'
import VtBubble from '@/components/common/VtBubble'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import { PatientOperationGuardMixin } from '@/api/operations/mixins/guards/patient-operation-guard.mixin'

export default {
    name: 'PatientTree',
    components: {
        VtBubble,
        CaseTree
    },
    mixins: [
        PatientOperationGuardMixin
    ],
    props: {
        laboratoryId: { type: Number, required: true },
        patientId: { type: Number, required: true },
        patient: { type: Object, required: true }
    },
    data() {
        return {
            patientAttachmentTree: { cases: [] },
            showArrow: true,
            hideCases: false,
            btnPressed: false,
            selectedElement: null,
            attachments: [],
            elementIcon: 'fa-user',
            attachmentEntityType: ATTACHMENT_ENTITY_TYPE.PATIENT
        }
    },
    computed: {
        entityId() {
            return this.patientId
        },
        entityDisplay() {
            return this.patientAttachmentTree.personIdentifier
        },
        userHasAttachOperation() {
            return this.$_canAttachDocumentToPatient()
        },
        canBeAttached() {
            return this.$_canBeDocumentAttachedToPatient(this.patient)
        }
    },
    methods: {
        async elementSelected() {
            this.deselect()
            this.btnPressed = true
            this.selectedElement = this
            const [err, response] = await this.toCall(patientAttachmentService.getAttachments(this.entityId))
            if (err) {
                this.showErrorModalGlobal()
                this.$emit('patient-tree-error')
                return
            }
            this.attachments = response
            this.$emit('child-selected', this)
        },
        childSelected(child) {
            if (child !== this.selectedElement) {
                this.deselect()
                this.selectedElement = child
            }
            if (child?.sample) {
                const elementToScroll = document.getElementById(child.sample?.samLabel)
                elementToScroll.scrollIntoView(false)
            } else {
                if (child?.complementary) {
                    const elementToScroll = document.getElementById(`complementary${child.complementary?.id}`)
                    elementToScroll.scrollIntoView(false)
                }
            }
            this.$emit('child-selected', child)
        },
        deselect() {
            if (this.selectedElement === this) {
                this.btnPressed = false
            } else if (this.selectedElement) {
                this.selectedElement.deselect()
            }
        },
        async loadPatientTree() {
            this.updateLoading(true)
            const withAttachments = true
            const withIncidents = false
            const [err, response] = await this.toCall(patientAttachmentService.getAttachmentPatientTree(this.laboratoryId, this.entityId, withAttachments, withIncidents))
            if (err) {
                this.showErrorModalGlobal()
                this.$emit('patient-tree-error')
            } else {
                this.patientAttachmentTree = response
            }
            this.updateLoading(false)
        },
        async showPatient() {
            await this.loadPatientTree()
            this.$nextTick(() => this.elementSelected())
        },
        async showCase(caseId) {
            await this.loadPatientTree()
            this.$nextTick(() => {
                const foundChild = this.findCaseTree(caseId)
                if (foundChild) {
                    foundChild.elementSelected()
                }
            })
        },
        findCaseTree(caseId) {
            if (!this.$refs.caseTree) {
                return null
            }
            return this.$refs.caseTree.find((child) => {
                return child.entityId === caseId
            })
        },
        async showSample(sampleId) {
            await this.loadPatientTree()
            this.$nextTick(() => {
                const foundChild = this.findSampleTree(sampleId)
                if (foundChild) {
                    foundChild.elementSelected()
                }
            })
        },
        findSampleTree(sampleId) {
            if (!this.$refs.caseTree) {
                return null
            }
            for (const caseTree of this.$refs.caseTree) {
                const sampleTree = caseTree.findSampleTree(sampleId)
                if (sampleTree) {
                    return sampleTree
                }
            }
        },
        async showComplementary(complementaryId) {
            await this.loadPatientTree()
            this.$nextTick(() => {
                const foundChild = this.findComplementaryTree(complementaryId)
                if (foundChild) {
                    foundChild.elementSelected()
                }
            })
        },
        findComplementaryTree(complementaryId) {
            if (!this.$refs.caseTree) {
                return null
            }
            for (const caseTree of this.$refs.caseTree) {
                const complementaryTree = caseTree.findComplementaryTree(complementaryId)
                if (complementaryTree) {
                    return complementaryTree
                }
            }
        },
        async showPortion(portionId) {
            await this.loadPatientTree()
            this.$nextTick(() => {
                const foundChild = this.findPortionTree(portionId)
                if (foundChild) {
                    foundChild.elementSelected()
                }
            })
        },
        findPortionTree(portionId) {
            if (!this.$refs.caseTree) {
                return null
            }
            for (const caseTree of this.$refs.caseTree) {
                const portionTree = caseTree.findPortionTree(portionId)
                if (portionTree) {
                    return portionTree
                }
            }
        },
        async showSubportion(subportionId) {
            await this.loadPatientTree()
            this.$nextTick(() => {
                const foundChild = this.findSubportionTree(subportionId)
                if (foundChild) {
                    foundChild.elementSelected()
                }
            })
        },
        findSubportionTree(subportionId) {
            if (!this.$refs.caseTree) {
                return null
            }
            for (const caseTree of this.$refs.caseTree) {
                const subportionTree = caseTree.findSubportionTree(subportionId)
                if (subportionTree) {
                    return subportionTree
                }
            }
        },
        async showChild(child) {
            await this.loadPatientTree()
            this.$nextTick(() => child.elementSelected())
        }
    }
}
</script>
